import type { AxiosResponse } from 'axios';
import type { Http } from '../http';
import { endpoints } from '../endpoints';
import type { RawFileDownload, RequestOptions } from '../types/common';
import type {
  RawErpExcelUploadResponse,
  RawErpRegisterParams,
  RawErpSettlementDeleteResponse,
  RawErpSettlementDetailResponse,
  RawErpSettlementListParams,
  RawErpSettlementListResponse,
} from '../types/erp';

/**
 * multipart 업로드 전용 config.
 *
 * ① `Content-Type: undefined` — v2 클라이언트는 헤더를 `application/json` 으로 **전역 고정**한다.
 *    그대로 두면 axios 가 FormData 에 boundary 를 못 붙여 BE 가 파트를 읽지 못한다. 여기서만 벗긴다.
 * ② `timeout` — 엑셀(최대 200MB) + PDF 다수를 올리고 BE 가 파싱·대조까지 끝낸 뒤 응답한다.
 *    소비처 기본(admin 60s)으로는 정상 요청이 끊긴다.
 */
const UPLOAD_CONFIG = {
  headers: { 'Content-Type': undefined },
  timeout: 180_000,
} as const;

export function createErpApi(http: Http) {
  return {
    /** GET /api/v4/erp/settlements — 정산월 목록(청구월 내림차순 고정). */
    async list(
      params?: RawErpSettlementListParams,
      options?: RequestOptions,
    ): Promise<RawErpSettlementListResponse> {
      const res = await http.get<RawErpSettlementListResponse>(endpoints.erp.list, {
        params,
        signal: options?.signal,
      });
      return res.data;
    },

    /** GET /api/v4/erp/settlements/{settlementId} — 단건 + 결의전표 전량. */
    async byId(
      settlementId: string,
      options?: RequestOptions,
    ): Promise<RawErpSettlementDetailResponse> {
      const res = await http.get<RawErpSettlementDetailResponse>(endpoints.erp.byId(settlementId), {
        signal: options?.signal,
      });
      return res.data;
    },

    /**
     * POST /api/v4/erp/settlements/register — 등록.
     * 파일만 multipart(`file` + `pdfs[]`), password·name·memo 는 **query** 다(스웨거 실측).
     */
    async register(
      file: File,
      pdfs: File[],
      params: RawErpRegisterParams,
      onProgress?: (percent: number) => void,
    ): Promise<RawErpExcelUploadResponse> {
      const form = new FormData();
      form.append('file', file);
      for (const pdf of pdfs) form.append('pdfs', pdf);
      const res = await http.post<RawErpExcelUploadResponse>(endpoints.erp.register, form, {
        ...UPLOAD_CONFIG,
        params,
        // 업로드 진행률 — **바이트 전송분만**이다. 100% 에 닿아도 BE 의 엑셀 파싱·PDF 대조가 남아 있어
        // 응답은 한참 뒤에 온다(그 구간은 진행률을 알 방법이 없다). 화면은 100% 를 '완료'로 읽지 말 것.
        // 전체 크기를 모르는 경우(total 없음)엔 아무것도 보고하지 않는다 — 0% 로 멈춘 듯 보이는 게 더 나쁘다.
        onUploadProgress: onProgress
          ? (event) => {
              if (!event.total) return;
              onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
            }
          : undefined,
      });
      return res.data;
    },

    /** DELETE /api/v4/erp/settlements/{settlementId} — 삭제. 응답 본문 의존 금지(캐시 무효화로 재조회). */
    async remove(settlementId: string): Promise<RawErpSettlementDeleteResponse> {
      const res = await http.delete<RawErpSettlementDeleteResponse>(
        endpoints.erp.remove(settlementId),
      );
      return res.data;
    },

    /** GET — 결제 엑셀 원본(.xlsx). 암호가 걸린 채 내려온다. */
    async downloadExcel(settlementId: string): Promise<RawFileDownload> {
      return toDownload(
        await http.get<Blob>(endpoints.erp.excel(settlementId), { responseType: 'blob' }),
      );
    },

    /** GET — 그 청구월 세금계산서 묶음(zip). 짝지어진 게 없으면 404. */
    async downloadInvoicesZip(settlementId: string): Promise<RawFileDownload> {
      return toDownload(
        await http.get<Blob>(endpoints.erp.invoicesZip(settlementId), { responseType: 'blob' }),
      );
    },

    /** GET — 세금계산서 단건(pdf). `invoiceId` = 홈택스 승인번호. */
    async downloadInvoicePdf(settlementId: string, invoiceId: string): Promise<RawFileDownload> {
      return toDownload(
        await http.get<Blob>(endpoints.erp.invoicePdf(settlementId, invoiceId), {
          responseType: 'blob',
        }),
      );
    },

    /** POST — 홈택스 묶음 PDF → 건별 zip. 화면 기능이 붙기 전이라 아직 소비처 없음. */
    async splitTaxInvoicePdf(pdfs: File[]): Promise<RawFileDownload> {
      const form = new FormData();
      for (const pdf of pdfs) form.append('pdfs', pdf);
      return toDownload(
        await http.post<Blob>(endpoints.erp.splitTaxInvoicePdf, form, {
          ...UPLOAD_CONFIG,
          responseType: 'blob',
        }),
      );
    },
  };
}

/**
 * 다운로드 응답 → `{blob, filename}`. 파일명은 **서버가 정한다**(엑셀은 올린 원본 이름, zip 은 날짜 포함) —
 * 화면이 지어내지 않도록 `Content-Disposition` 에서 읽어 그대로 싣는다. 못 읽으면 빈 문자열(호출처 폴백).
 */
function toDownload(res: AxiosResponse<Blob>): RawFileDownload {
  return { blob: res.data, filename: filenameOf(res.headers['content-disposition']) };
}

function filenameOf(disposition: unknown): string {
  if (typeof disposition !== 'string') return '';
  // RFC 5987 `filename*=UTF-8''...` 우선(한글 파일명) — 없으면 평문 `filename="..."`.
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      /* 잘못 인코딩된 헤더 — 평문 쪽으로 폴백 */
    }
  }
  return /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? '';
}
